import {
  ClockCircleOutlined,
  CrownFilled,
  CrownOutlined,
  EditOutlined,
  ExportOutlined,
  FieldTimeOutlined,
  HolderOutlined,
  InfoCircleOutlined,
  MoreOutlined,
  ScheduleOutlined,
  SwapOutlined,
  UserAddOutlined,
  UserDeleteOutlined,
  WarningFilled,
} from '@ant-design/icons';
import { useMutation } from '@tanstack/react-query';
import type { MenuProps } from 'antd';
import { Alert, Avatar, DatePicker, Dropdown, Empty, Flex, Input, Modal, Space, Tag, Tooltip, Typography } from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import React, { useState } from 'react';
import type { Membership, Team } from '../../DTOS/ResourcePlanning';
import api from '../../services/resourcePlanning/ResourcePlanningService';
import {
  colorOf,
  DATE,
  DISPLAY_DATE,
  hours,
  IconButton,
  initials,
  membersOn,
  PlanningLink,
  teamPayload,
  transferMessage,
  useFeedback,
} from './shared';

type Kind = 'move' | 'swap' | 'end';
interface Pending {
  kind: Kind;
  member: Membership;
  from: Team;
  to?: Team;
  other?: Membership;
}
interface Dragged {
  member: Membership;
  team: Team;
}
const COLUMN = 270;

/**
 * Teams as columns of people on a chosen date. Drag a person onto another team to move them,
 * or onto a person of another team to swap both. Every change is confirmed with its effect first.
 */
const TeamBoard: React.FC<{
  teams: Team[];
  onAdd: (team: Team) => void;
  onEdit: (team: Team) => void;
  /** Without a member: the start date of the whole team. */
  onPeriod: (team: Team, member?: Membership) => void;
  /** Team leads see the board without drag and drop or change actions. */
  readOnly?: boolean;
  /** Allocate the whole team (without a member) or one member on a project, from the board's date. */
  onAllocate?: (team: Team, member: Membership | undefined, date: string) => void;
  /** Planners allocate every team; a team lead only the teams they lead. */
  canAllocate?: (team: Team) => boolean;
}> = ({ teams, onAdd, onEdit, onPeriod, readOnly = false, onAllocate, canAllocate = () => false }) => {
  const feedback = useFeedback();
  const [date, setDate] = useState<Dayjs>(dayjs());
  const [search, setSearch] = useState('');
  const [dragged, setDragged] = useState<Dragged | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const [effective, setEffective] = useState<Dayjs>(dayjs());
  const run = useMutation({ mutationFn: (fn: () => Promise<unknown>) => fn(), onError: feedback.error });
  const day = date.format(DATE);
  const columns = teams.filter((team) => team.isActive).sort((a, b) => a.name.localeCompare(b.name));
  const members = new Map(columns.map((team) => [team.id, membersOn(team, day)]));
  const term = search.trim().toLowerCase();

  const ask = (next: Pending) => {
    setEffective(date);
    setPending(next);
  };
  const finishDrag = () => {
    setDragged(null);
    setOver(null);
  };
  const setLeader = (team: Team, userId: string | null) =>
    run.mutate(async () => {
      await api.saveTeam({ ...teamPayload(team), leaderUserId: userId });
      feedback.success(userId ? 'Sef de echipa actualizat.' : 'Rolul de sef a fost retras.');
    });
  const confirm = () =>
    run.mutate(async () => {
      const { kind, member, to, other } = pending!;
      const effectiveFrom = effective.format(DATE);
      const result =
        kind === 'swap'
          ? await api.swap({ userId: member.userId, otherUserId: other!.userId, effectiveFrom })
          : await api.move({ userId: member.userId, teamId: kind === 'end' ? null : to!.id, effectiveFrom });
      feedback.success(transferMessage(result.affectedAllocations));
      setPending(null);
    });

  const menu = (team: Team, member: Membership): MenuProps['items'] => {
    const others = columns.filter((item) => item.id !== team.id);
    const leader = team.leaderUserId === member.userId;
    return [
      ...(onAllocate && canAllocate(team)
        ? [
            {
              key: 'allocate',
              icon: <ScheduleOutlined />,
              label: 'Aloca pe un proiect',
              onClick: () => onAllocate(team, member, day),
            },
            { type: 'divider' as const },
          ]
        : []),
      {
        key: 'chief',
        icon: leader ? <CrownFilled style={{ color: '#faad14' }} /> : <CrownOutlined />,
        // The server gives a new chief the ERP role "Sef echipa proiect".
        label: leader ? 'Retrage rolul de sef' : 'Seteaza ca sef echipa',
        onClick: () => setLeader(team, leader ? null : member.userId),
      },
      {
        key: 'period',
        icon: <FieldTimeOutlined />,
        label: 'Modifica perioada',
        onClick: () => onPeriod(team, member),
      },
      {
        key: 'move',
        icon: <ExportOutlined />,
        label: 'Muta in',
        disabled: !others.length,
        children: others.map((item) => ({
          key: `move:${item.id}`,
          label: item.name,
          onClick: () => ask({ kind: 'move', member, from: team, to: item }),
        })),
      },
      {
        key: 'swap',
        icon: <SwapOutlined />,
        label: 'Schimba cu',
        disabled: !others.length,
        children: others.map((item) => ({
          key: `swap:${item.id}`,
          label: item.name,
          disabled: !members.get(item.id)!.length,
          children: members
            .get(item.id)!
            .map((other) => ({
              key: `swap:${item.id}:${other.userId}`,
              label: other.userName,
              onClick: () => ask({ kind: 'swap', member, from: team, to: item, other }),
            })),
        })),
      },
      { type: 'divider' },
      {
        key: 'end',
        icon: <UserDeleteOutlined />,
        danger: true,
        label: 'Scoate din echipa',
        onClick: () => ask({ kind: 'end', member, from: team }),
      },
    ];
  };

  const effect = (team: Team, delta: number) => {
    const count = members.get(team.id)!.length;
    return (
      <div key={team.id}>
        <strong>{team.name}</strong>: {count} → {count + delta} persoane · {hours(count * team.dailyMinutes)} →{' '}
        {hours((count + delta) * team.dailyMinutes)} / zi
      </div>
    );
  };
  const chiefLoss = (team: Team, member?: Membership) =>
    member && team.leaderUserId === member.userId ? (
      <div key={`chief:${team.id}`}>
        <WarningFilled style={{ color: '#fa8c16' }} /> {member.userName} nu va mai fi sef al echipei {team.name}.
      </div>
    ) : null;
  const titles: Record<Kind, string> = {
    move: 'Mutare in alta echipa',
    swap: 'Schimb intre echipe',
    end: 'Scoatere din echipa',
  };

  if (!columns.length) return <Empty description='Nicio echipa activa. Creati o echipa pentru a incepe.' />;
  return (
    <Space direction='vertical' size='middle' style={{ width: '100%' }}>
      <Flex wrap gap={8} align='center'>
        <Space.Compact>
          <span
            style={{
              padding: '4px 11px',
              border: '1px solid #d9d9d9',
              borderRight: 0,
              borderRadius: '6px 0 0 6px',
              background: '#fafafa',
            }}
          >
            Situatia la
          </span>
          <DatePicker
            value={date}
            onChange={(value) => value && setDate(value)}
            allowClear={false}
            format={DISPLAY_DATE}
          />
        </Space.Compact>
        <Input.Search
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder='Cauta persoana'
          allowClear
          style={{ width: 220 }}
        />
        <Typography.Text type='secondary'>
          <InfoCircleOutlined /> Trageti o persoana pe alta echipa pentru mutare sau peste o persoana din alta echipa
          pentru schimb.
        </Typography.Text>
      </Flex>

      <div style={{ display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 8, alignItems: 'flex-start' }}>
        {columns.map((team) => {
          const people = members.get(team.id)!;
          const scheduled = team.memberships.filter(
            (member) => member.validFrom > day && !people.some((person) => person.userId === member.userId),
          );
          const target = !!dragged && dragged.team.id !== team.id;
          return (
            <div
              key={team.id}
              style={{
                width: COLUMN,
                minWidth: COLUMN,
                borderRadius: 8,
                background: over === team.id ? '#e6f4ff' : '#fafafa',
                border: `1px ${target ? 'dashed' : 'solid'} ${over === team.id ? '#1677ff' : target ? '#91caff' : '#f0f0f0'}`,
                transition: 'background .15s',
              }}
              onDragOver={(event) => {
                if (target) {
                  event.preventDefault();
                  if (over !== team.id) setOver(team.id);
                }
              }}
              onDragLeave={(event) => {
                if (!event.currentTarget.contains(event.relatedTarget as Node)) setOver(null);
              }}
              onDrop={(event) => {
                event.preventDefault();
                if (target) ask({ kind: 'move', member: dragged!.member, from: dragged!.team, to: team });
                finishDrag();
              }}
            >
              <div style={{ padding: '10px 12px', borderBottom: '1px solid #f0f0f0' }}>
                <Flex justify='space-between' align='center'>
                  <Typography.Text strong ellipsis style={{ maxWidth: 150 }}>
                    {team.name}
                  </Typography.Text>
                  <Space size={2}>
                    {onAllocate && canAllocate(team) && (
                      <IconButton
                        title='Aloca toata echipa pe un proiect'
                        size='small'
                        type='text'
                        icon={<ScheduleOutlined />}
                        disabled={!people.length}
                        onClick={() => onAllocate(team, undefined, day)}
                      />
                    )}
                    {!readOnly && (
                      <>
                        <IconButton
                          title='Adauga membri'
                          size='small'
                          type='text'
                          icon={<UserAddOutlined />}
                          onClick={() => onAdd(team)}
                        />
                        <IconButton
                          title='Data de inceput pentru toata echipa'
                          size='small'
                          type='text'
                          icon={<FieldTimeOutlined />}
                          onClick={() => onPeriod(team)}
                        />
                        <IconButton
                          title='Editeaza echipa'
                          size='small'
                          type='text'
                          icon={<EditOutlined />}
                          onClick={() => onEdit(team)}
                        />
                      </>
                    )}
                  </Space>
                </Flex>
                <Space size={4} wrap style={{ marginTop: 4 }}>
                  <Tag bordered={false} icon={<ClockCircleOutlined />}>
                    {hours(team.dailyMinutes)} / zi
                  </Tag>
                  <Tooltip title='Capacitate zilnica a echipei'>
                    <Tag bordered={false} color='blue'>
                      {people.length} pers. · {hours(people.length * team.dailyMinutes)}
                    </Tag>
                  </Tooltip>
                  {scheduled.length > 0 && (
                    <Tooltip
                      title={scheduled
                        .map((member) => `${member.userName} din ${dayjs(member.validFrom).format(DISPLAY_DATE)}`)
                        .join(', ')}
                    >
                      <Tag bordered={false} color='purple'>
                        +{scheduled.length} programati
                      </Tag>
                    </Tooltip>
                  )}
                </Space>
                <PlanningLink teamId={team.id} date={day} name={team.name} />
              </div>
              <div style={{ padding: 8, minHeight: 80, display: 'flex', flexDirection: 'column', gap: 6 }}>
                {!people.length && (
                  <Typography.Text type='secondary' style={{ textAlign: 'center', padding: 16, fontSize: 12 }}>
                    Niciun membru la aceasta data
                  </Typography.Text>
                )}
                {people.map((member) => {
                  const leader = team.leaderUserId === member.userId,
                    swapTarget = target && over === member.id;
                  const dimmed = term && !member.userName.toLowerCase().includes(term);
                  return (
                    <div
                      key={member.id}
                      draggable={!readOnly}
                      onDragStart={(event) => {
                        event.dataTransfer.effectAllowed = 'move';
                        event.dataTransfer.setData('text/plain', member.userId);
                        setDragged({ member, team });
                      }}
                      onDragEnd={finishDrag}
                      onDragOver={(event) => {
                        if (target) {
                          event.preventDefault();
                          event.stopPropagation();
                          if (over !== member.id) setOver(member.id);
                        }
                      }}
                      onDrop={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        if (target)
                          ask({ kind: 'swap', member: dragged!.member, from: dragged!.team, to: team, other: member });
                        finishDrag();
                      }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '6px 8px',
                        background: '#fff',
                        borderRadius: 6,
                        cursor: readOnly ? 'default' : 'grab',
                        border: `1px solid ${swapTarget ? '#722ed1' : leader ? '#ffe58f' : '#f0f0f0'}`,
                        boxShadow: swapTarget ? '0 0 0 2px #d3adf7' : '0 1px 2px rgba(0,0,0,0.04)',
                        opacity: dragged?.member.id === member.id ? 0.4 : dimmed ? 0.35 : 1,
                      }}
                    >
                      {!readOnly && <HolderOutlined style={{ color: '#bfbfbf' }} />}
                      <Avatar size='small' style={{ background: colorOf(member.userId), flex: 'none' }}>
                        {initials(member.userName)}
                      </Avatar>
                      <Typography.Text ellipsis style={{ flex: 1 }}>
                        {member.userName}
                      </Typography.Text>
                      {swapTarget && (
                        <Tag color='purple' bordered={false} icon={<SwapOutlined />}>
                          schimb
                        </Tag>
                      )}
                      {leader && (
                        <Tooltip title='Sef echipa'>
                          <CrownFilled style={{ color: '#faad14' }} />
                        </Tooltip>
                      )}
                      {readOnly && onAllocate && canAllocate(team) && (
                        <IconButton
                          title='Aloca pe un proiect'
                          size='small'
                          type='text'
                          icon={<ScheduleOutlined />}
                          onClick={() => onAllocate(team, member, day)}
                        />
                      )}
                      {!readOnly && (
                        <Dropdown menu={{ items: menu(team, member) }} trigger={['click']}>
                          <IconButton
                            title='Actiuni'
                            size='small'
                            type='text'
                            icon={<MoreOutlined />}
                            loading={run.isPending && !pending}
                          />
                        </Dropdown>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      <Modal
        open={!!pending}
        title={pending && titles[pending.kind]}
        onCancel={() => setPending(null)}
        onOk={confirm}
        confirmLoading={run.isPending}
        okText='Confirma'
        cancelText='Renunta'
        okButtonProps={{ danger: pending?.kind === 'end' }}
        destroyOnHidden
      >
        {pending && (
          <Space direction='vertical' size='middle' style={{ width: '100%' }}>
            <Typography.Text>
              {pending.kind === 'move' && (
                <>
                  <strong>{pending.member.userName}</strong> trece din <strong>{pending.from.name}</strong> in{' '}
                  <strong>{pending.to!.name}</strong>.
                </>
              )}
              {pending.kind === 'swap' && (
                <>
                  <strong>{pending.member.userName}</strong> ({pending.from.name}) <SwapOutlined />{' '}
                  <strong>{pending.other!.userName}</strong> ({pending.to!.name}).
                </>
              )}
              {pending.kind === 'end' && (
                <>
                  <strong>{pending.member.userName}</strong> nu va mai face parte din{' '}
                  <strong>{pending.from.name}</strong>.
                </>
              )}
            </Typography.Text>
            <Space>
              Incepand cu
              <DatePicker
                value={effective}
                onChange={(value) => value && setEffective(value)}
                allowClear={false}
                format={DISPLAY_DATE}
              />
            </Space>
            <div style={{ fontSize: 13 }}>
              {pending.kind === 'move' && [effect(pending.from, -1), effect(pending.to!, 1)]}
              {pending.kind === 'end' && effect(pending.from, -1)}
              {pending.kind === 'swap' && pending.from.dailyMinutes !== pending.to!.dailyMinutes && (
                <div>
                  Programul zilnic difera: {pending.from.name} {hours(pending.from.dailyMinutes)}, {pending.to!.name}{' '}
                  {hours(pending.to!.dailyMinutes)}.
                </div>
              )}
              {chiefLoss(pending.from, pending.member)}
              {pending.to && chiefLoss(pending.to, pending.other)}
            </div>
            <Alert
              type='info'
              showIcon
              message='Alocarile existente raman neschimbate. Pentru un imprumut temporar, alocati persoana direct in Planificare.'
            />
          </Space>
        )}
      </Modal>
    </Space>
  );
};
export default TeamBoard;
